/**
 * Abwesenheiten (/abwesenheiten): Filterleiste (Zeitraum/Mitarbeiter/Grund),
 * Ergebnisliste mit Grund-Badges (10 feste Farben, lib/absenceColors.ts),
 * Monats-Kalenderansicht (Balken je Mitarbeiter) sowie Massenbuchung (G-20)
 * und Massen-Loeschung (G-21).
 */

import { useMemo, useState } from "react"
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { CalendarRange, Plus, Trash2 } from "lucide-react"
import { toast } from "sonner"
import { AbsenceFormSheet } from "@/components/absences/AbsenceFormSheet"
import { ApplyClosureDialog } from "@/components/absences/ApplyClosureDialog"
import { DAY_TYPE_TINT } from "@/components/calendar/dayTypes"
import { BusinessDayRangePicker, LoadingSpinner } from "@/components/shared"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { DataGrid, type ColumnDef } from "@/components/ui/data-grid"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import {
  ABSENCE_REASONS,
  ABSENCE_REASON_VALUES,
  ABSENCE_STATUS_LABEL,
} from "@/lib/absenceColors"
import { fmtDate } from "@/lib/format"
import { useCan } from "@/lib/permissions"
import { toDayMap, useDayInfos } from "@/lib/useDayInfos"
import { cn } from "@/lib/utils"
import { api, apiError } from "@/services/api"
import type { Absence, AbwesenheitGrund } from "@/types"

// ---------------------------------------------------------------------------
// Datums-Helpers (lokale Kalenderdaten als "YYYY-MM-DD"-Strings, kein Date-Shift)
// ---------------------------------------------------------------------------

const pad = (n: number) => String(n).padStart(2, "0")

function currentMonthRange(): { from: string; to: string } {
  const now = new Date()
  const y = now.getFullYear()
  const m = now.getMonth() + 1
  const lastDay = new Date(y, m, 0).getDate()
  return { from: `${y}-${pad(m)}-01`, to: `${y}-${pad(m)}-${pad(lastDay)}` }
}

/** Alle Tage des Monats, in dem `anchor` ("YYYY-MM-DD") liegt */
function monthDays(anchor: string): { iso: string; day: number; isWeekend: boolean }[] {
  const m = /^(\d{4})-(\d{2})/.exec(anchor)
  if (!m) return []
  const year = Number(m[1])
  const month = Number(m[2])
  const count = new Date(year, month, 0).getDate()
  return Array.from({ length: count }, (_, i) => {
    const day = i + 1
    const weekday = new Date(year, month - 1, day).getDay()
    return {
      iso: `${year}-${pad(month)}-${pad(day)}`,
      day,
      isWeekend: weekday === 0 || weekday === 6,
    }
  })
}

// ---------------------------------------------------------------------------
// Massen-Loeschung (G-21)
// ---------------------------------------------------------------------------

function BulkDeleteDialog({
  open,
  onOpenChange,
  employees,
  defaultFrom,
  defaultTo,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  employees: { id: string; displayName: string }[]
  defaultFrom: string
  defaultTo: string
}) {
  const queryClient = useQueryClient()
  const [employeeId, setEmployeeId] = useState("")
  const [from, setFrom] = useState(defaultFrom)
  const [to, setTo] = useState(defaultTo)

  const del = useMutation({
    mutationFn: () =>
      api.absences.bulkDelete({ employeeId: Number(employeeId), from, to }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["absences"] })
      queryClient.invalidateQueries({ queryKey: ["employees"] })
      toast.success(`${result.deleted} Abwesenheit(en) gelöscht`)
      onOpenChange(false)
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const valid = employeeId !== "" && from !== "" && to !== "" && from <= to

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Abwesenheiten im Zeitraum löschen</DialogTitle>
          <DialogDescription>
            Löscht ALLE Abwesenheiten des Mitarbeiters, die den Zeitraum schneiden (G-21).
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="space-y-1.5">
            <Label>Mitarbeiter *</Label>
            <Select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
              <option value="">— Mitarbeiter wählen —</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.displayName}
                </option>
              ))}
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Von *</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Bis *</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>
          <BusinessDayRangePicker
            from={from}
            to={to}
            onChange={(f, t) => {
              setFrom(f)
              setTo(t)
            }}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button variant="destructive" disabled={!valid || del.isPending} onClick={() => del.mutate()}>
            Löschen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Monats-Kalender: Tage × Mitarbeiter, Abwesenheiten als farbige Balkenzellen
// ---------------------------------------------------------------------------

function MonthCalendar({
  anchor,
  absences,
  employeeName,
}: {
  anchor: string
  absences: Absence[]
  employeeName: (id: string) => string
}) {
  const days = monthDays(anchor)

  // Tages-Hintergruende aus dem Betriebskalender (EIN dayInfos-Call pro Monat)
  const anchorMatch = /^(\d{4})-(\d{2})/.exec(anchor)
  const dayInfos = useDayInfos(
    anchorMatch ? Number(anchorMatch[1]) : 0,
    anchorMatch ? Number(anchorMatch[2]) : 0,
    anchorMatch !== null
  )
  const dayMap = useMemo(() => toDayMap(dayInfos.data), [dayInfos.data])
  const byEmployee = useMemo(() => {
    const map = new Map<string, Absence[]>()
    for (const a of absences) {
      const list = map.get(a.employeeId) ?? []
      list.push(a)
      map.set(a.employeeId, list)
    }
    return [...map.entries()].sort((x, y) =>
      employeeName(x[0]).localeCompare(employeeName(y[0]), "de")
    )
  }, [absences, employeeName])

  if (days.length === 0) return null

  return (
    <Card>
      <CardContent className="overflow-x-auto py-4">
        {byEmployee.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Keine Abwesenheiten im gewählten Monat.
          </p>
        ) : (
          <div className="min-w-[720px] space-y-1">
            {/* Kopfzeile: Tagesnummern */}
            <div
              className="grid gap-px text-center text-[10px] text-muted-foreground"
              style={{ gridTemplateColumns: `10rem repeat(${days.length}, minmax(0, 1fr))` }}
            >
              <div />
              {days.map((d) => {
                const info = dayMap.get(d.iso)
                const offDay = info ? !info.isPlannable : d.isWeekend
                return (
                  <div
                    key={d.iso}
                    title={info?.holidayName ?? undefined}
                    className={cn(offDay && "text-muted-foreground/50")}
                  >
                    {d.day}
                  </div>
                )
              })}
            </div>
            {/* Zeile je Mitarbeiter */}
            {byEmployee.map(([employeeId, list]) => (
              <div
                key={employeeId}
                className="grid items-center gap-px"
                style={{ gridTemplateColumns: `10rem repeat(${days.length}, minmax(0, 1fr))` }}
              >
                <div className="truncate pr-2 text-xs font-medium">
                  {employeeName(employeeId)}
                </div>
                {days.map((d) => {
                  const absence = list.find((a) => a.startDate <= d.iso && d.iso <= a.endDate)
                  const info = dayMap.get(d.iso)
                  const tint = info
                    ? DAY_TYPE_TINT[info.dayType]
                    : d.isWeekend
                      ? "bg-muted/60"
                      : "bg-muted/25"
                  return (
                    <div
                      key={d.iso}
                      className={cn(
                        "h-5 rounded-sm",
                        absence ? ABSENCE_REASONS[absence.reason].bar : tint
                      )}
                      title={
                        absence
                          ? `${employeeName(employeeId)}: ${ABSENCE_REASONS[absence.reason].label} (${fmtDate(absence.startDate)} – ${fmtDate(absence.endDate)})`
                          : (info?.holidayName ?? undefined)
                      }
                    />
                  )
                })}
              </div>
            ))}
            {/* Legende */}
            <div className="flex flex-wrap gap-3 pt-3">
              {ABSENCE_REASON_VALUES.map((r) => (
                <span key={r} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className={cn("size-3 rounded-sm", ABSENCE_REASONS[r].bar)} />
                  {ABSENCE_REASONS[r].label}
                </span>
              ))}
              {(
                [
                  ["FEIERTAG", "Feiertag"],
                  ["BETRIEBSURLAUB", "Betriebsurlaub"],
                  ["WOCHENENDE", "Wochenende"],
                ] as const
              ).map(([t, label]) => (
                <span key={t} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className={cn("size-3 rounded-sm border", DAY_TYPE_TINT[t])} />
                  {label}
                </span>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Seite
// ---------------------------------------------------------------------------

export function AbsencesPage() {
  const canWrite = useCan("personal_schreiben")
  const initial = useMemo(currentMonthRange, [])

  const [from, setFrom] = useState(initial.from)
  const [to, setTo] = useState(initial.to)
  const [employeeFilter, setEmployeeFilter] = useState("")
  const [reasonFilter, setReasonFilter] = useState<AbwesenheitGrund | "">("")
  const [createOpen, setCreateOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [applyClosureOpen, setApplyClosureOpen] = useState(false)

  const employees = useQuery({
    queryKey: ["employees", { size: 100 }],
    queryFn: () => api.employees.list({ size: 100 }),
  })
  const employeeName = (id: string) =>
    employees.data?.data.find((e) => e.id === id)?.displayName ?? `Mitarbeiter #${id}`

  const params = {
    size: 100,
    from: from || undefined,
    to: to || undefined,
    employee: employeeFilter ? Number(employeeFilter) : undefined,
    reason: reasonFilter || undefined,
  }
  const query = useQuery({
    queryKey: ["absences", params],
    queryFn: () => api.absences.list(params),
    placeholderData: keepPreviousData,
  })

  const rows = query.data?.data ?? []

  // Grid-Spalten: header als String, damit die Spalten-Auswahl ihn anzeigen kann
  const columns = useMemo<ColumnDef<Absence>[]>(
    () => [
      {
        id: "employee",
        header: "Mitarbeiter",
        accessorFn: (a) => employeeName(a.employeeId),
        meta: { cellClassName: "font-medium" },
      },
      {
        id: "period",
        // Datums-Spalte: ISO-String als Accessor sortiert korrekt
        header: "Zeitraum",
        accessorFn: (a) => a.startDate,
        cell: ({ row }) => (
          <>
            {fmtDate(row.original.startDate)} – {fmtDate(row.original.endDate)}
          </>
        ),
        meta: { cellClassName: "tabular-nums" },
      },
      {
        id: "reason",
        header: "Grund",
        accessorFn: (a) => ABSENCE_REASONS[a.reason].label,
        cell: ({ row }) => (
          <Badge variant="outline" className={ABSENCE_REASONS[row.original.reason].badge}>
            {ABSENCE_REASONS[row.original.reason].label}
          </Badge>
        ),
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (a) => ABSENCE_STATUS_LABEL[a.status],
        meta: { cellClassName: "text-muted-foreground" },
      },
    ],
    // employeeName haengt an der Mitarbeiterliste
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [employees.data],
  )

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold tracking-tight">Abwesenheiten</h1>
        {canWrite && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setApplyClosureOpen(true)}>
              <CalendarRange /> Betriebsurlaub anwenden
            </Button>
            <Button variant="outline" onClick={() => setDeleteOpen(true)}>
              <Trash2 /> Massen-Löschung
            </Button>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus /> Abwesenheit buchen
            </Button>
          </div>
        )}
      </div>

      {/* Filterleiste */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label>Von</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Bis</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div className="w-56 space-y-1.5">
          <Label>Mitarbeiter</Label>
          <Select value={employeeFilter} onChange={(e) => setEmployeeFilter(e.target.value)}>
            <option value="">Alle Mitarbeiter</option>
            {(employees.data?.data ?? []).map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.displayName}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-48 space-y-1.5">
          <Label>Grund</Label>
          <Select
            value={reasonFilter}
            onChange={(e) => setReasonFilter(e.target.value as AbwesenheitGrund | "")}
          >
            <option value="">Alle Gründe</option>
            {ABSENCE_REASON_VALUES.map((r) => (
              <option key={r} value={r}>
                {ABSENCE_REASONS[r].label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Monats-Kalender (Monat des Von-Datums) */}
      <MonthCalendar anchor={from || initial.from} absences={rows} employeeName={employeeName} />

      {/* Ergebnisliste */}
      <Card>
        <CardContent className="py-2">
          {query.isLoading ? (
            <LoadingSpinner fullPage />
          ) : (
            <DataGrid
              columns={columns}
              data={rows}
              storageKey="absences"
              emptyMessage="Keine Abwesenheiten im gewählten Zeitraum."
            />
          )}
        </CardContent>
      </Card>

      <AbsenceFormSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        defaultFrom={from}
        defaultTo={to}
      />
      {deleteOpen && (
        <BulkDeleteDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          employees={employees.data?.data ?? []}
          defaultFrom={from}
          defaultTo={to}
        />
      )}
      {applyClosureOpen && (
        <ApplyClosureDialog
          open={applyClosureOpen}
          onOpenChange={setApplyClosureOpen}
          defaultFrom={from}
          defaultTo={to}
        />
      )}
    </div>
  )
}
